import { SelectField } from '@cinch-labs/design-system'
import { useCarReviewsHubTracking } from '@cinch-nx/data-tracking'

import styles from './filters.module.css'

import { ChangeEvent } from 'react'

export interface FiltersProps {
  selectedMake?: string | null
  selectedModel?: string | null
  makes: string[]
  models?: string[]
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  isISR?: boolean
}

type FilterChangeTracker = ReturnType<
  typeof useCarReviewsHubTracking
>['trackOnFilterChange']

function onMakeChange({
  setIsLoading,
  trackOnFilterChange,
  isISR = false,
}: {
  setIsLoading: (i: boolean) => void
  trackOnFilterChange: FilterChangeTracker
  isISR?: boolean
}) {
  return (event: ChangeEvent<HTMLSelectElement>) => {
    setIsLoading(true)
    trackOnFilterChange(event.currentTarget.value, 'make')

    const rawParams: Record<string, string> = {}

    if (event.currentTarget.value) {
      rawParams.make = event.currentTarget.value
    }

    if (isISR) {
      window.location.href = `/reviews/${rawParams.make}`
      return
    }

    const urlParams = new URLSearchParams(rawParams)

    window.location.href = `/reviews?${urlParams.toString()}`
  }
}

function onModelChange({
  selectedMake,
  setIsLoading,
  trackOnFilterChange,
  isISR = false,
}: {
  selectedMake: string
  setIsLoading: (i: boolean) => void
  trackOnFilterChange: FilterChangeTracker
  isISR?: boolean
}) {
  return (event: ChangeEvent<HTMLSelectElement>) => {
    setIsLoading(true)
    trackOnFilterChange(event.currentTarget.value, 'model')

    const rawParams: Record<string, string> = {
      make: selectedMake,
    }

    if (event.currentTarget.value) {
      rawParams.model = event.currentTarget.value
    }

    if (isISR) {
      window.location.href = `/reviews/${rawParams.make}/${rawParams.model}`
      return
    }

    const urlParams = new URLSearchParams(rawParams)

    window.location.href = `/reviews${
      urlParams.toString() ? `?${urlParams.toString()}` : ''
    }`
  }
}

export const Filters: React.FC<FiltersProps> = ({
  selectedMake = '',
  selectedModel = '',
  makes = [],
  models = [],
  isLoading,
  setIsLoading,
  isISR = false,
}) => {
  const { trackOnFilterChange } = useCarReviewsHubTracking()

  const selectFieldClassNames = {
    container: styles.selectContainer,
    select: styles.select,
  }

  selectedMake = selectedMake ?? ''
  selectedModel = selectedModel ?? ''

  return (
    <section className={styles.container}>
      <SelectField
        id="make-options"
        placeholder="All makes"
        value={selectedMake}
        disabled={isLoading}
        options={[
          { label: 'All makes', value: '' },
          ...makes.map((make) => ({
            label: make,
            value: make,
          })),
        ]}
        label="Filter by make"
        classNames={selectFieldClassNames}
        onChange={onMakeChange({
          setIsLoading,
          trackOnFilterChange,
          isISR,
        })}
      />
      <SelectField
        id="model-options"
        placeholder="All models"
        value={selectedModel}
        options={[
          { label: 'All models', value: '' },
          ...models.map((model) => ({
            label: model,
            value: model,
          })),
        ]}
        label="Filter by model"
        classNames={selectFieldClassNames}
        disabled={isLoading || selectedMake === ''}
        onChange={onModelChange({
          selectedMake,
          setIsLoading,
          trackOnFilterChange,
          isISR,
        })}
      />
    </section>
  )
}
